import dayjs from "dayjs";
import { seededRatio, seededSwing } from "src/utils/seededMockRandom";
import type { PaidMediaOverviewPlatform } from "src/services/PaidMediaOverviewAPI";

/**
 * Centralised mock dataset for paid media. Every mocked number is derived from
 * the daily fact table below, so the ratios always reconcile, and the jitter is
 * seeded rather than random, so a given (platform, date) never changes.
 */

export const PAID_MEDIA_MOCK_CURRENCY = "SAR";

export interface MockDailyFact {
  date: string;
  spend: number;
  impressions: number;
  clicks: number;
  purchases: number;
  revenue: number;
}

export interface MockTotals {
  spend: number;
  impressions: number;
  clicks: number;
  purchases: number;
  revenue: number;
}

interface PlatformProfile {
  /** Mean daily spend in SAR. */
  dailySpend: number;
  /** Cost per 1,000 impressions in SAR. */
  cpm: number;
  /** Click-through rate as a fraction (swipe-up rate for Snapchat). */
  ctr: number;
  /** Click-to-purchase rate as a fraction. */
  clickToPurchase: number;
  /** Average order value in SAR. */
  aov: number;
  /** Extra spend on Thu–Sat (the KSA weekend runs Fri–Sat). */
  weekendLift: number;
  /** Daily compounding budget growth, anchored to `TREND_ANCHOR_DATE`. */
  budgetGrowth: number;
  /** Daily compounding change in conversion efficiency. */
  efficiencyGrowth: number;
  /** Amplitude of the seeded day-to-day jitter. */
  volatility: number;
}

/** Growth is measured from here, so the prior period sits lower on the curve. */
const TREND_ANCHOR_DATE = "2026-08-24";
const MAX_GROWTH_FACTOR = 3;
const MIN_GROWTH_FACTOR = 0.3;

/**
 * Meta carries the scale, Google leads efficiency, TikTok is the growth channel
 * and Snapchat the smallest. ROAS falls out as (1000 / cpm) x ctr x cvr x aov.
 */
const PLATFORM_PROFILES: Record<PaidMediaOverviewPlatform, PlatformProfile> = {
  meta_ads: {
    dailySpend: 46_000,
    cpm: 30,
    ctr: 0.0101,
    clickToPurchase: 0.022,
    aov: 620,
    weekendLift: 0.12,
    budgetGrowth: 0.0009,
    efficiencyGrowth: 0.0004,
    volatility: 0.16,
  },
  google_ads: {
    dailySpend: 20_650,
    cpm: 90,
    ctr: 0.035,
    clickToPurchase: 0.023,
    aov: 700,
    weekendLift: 0.05,
    budgetGrowth: 0.0016,
    efficiencyGrowth: 0.0007,
    volatility: 0.12,
  },
  tiktok_ads: {
    dailySpend: 13_650,
    cpm: 18,
    ctr: 0.009,
    clickToPurchase: 0.0131,
    aov: 520,
    weekendLift: 0.18,
    budgetGrowth: 0.0042,
    efficiencyGrowth: 0.0013,
    volatility: 0.21,
  },
  snapchat_ads: {
    dailySpend: 8_650,
    cpm: 14,
    ctr: 0.0075,
    clickToPurchase: 0.0109,
    aov: 480,
    weekendLift: 0.22,
    budgetGrowth: -0.0006,
    efficiencyGrowth: -0.0009,
    volatility: 0.24,
  },
};

/* ---------------- deterministic pseudo-randomness ---------------- */

/** Seeded value in [0, 1] — shared with the per-platform report mocks. */
export const mockSeededRatio = seededRatio;

const round2 = (value: number): number => Math.round(value * 100) / 100;

/* ---------------- date helpers ---------------- */

const MAX_MOCK_DAYS = 400;

export function mockDateRange(startDate: string, endDate: string): string[] {
  const start = dayjs(startDate);
  const end = dayjs(endDate);
  if (!start.isValid() || !end.isValid() || end.isBefore(start)) {
    return [];
  }

  const dates: string[] = [];
  let cursor = start;
  while (!cursor.isAfter(end) && dates.length < MAX_MOCK_DAYS) {
    dates.push(cursor.format("YYYY-MM-DD"));
    cursor = cursor.add(1, "day");
  }
  return dates;
}

/** The equal-length window immediately before [startDate, endDate]. */
export function mockPriorPeriod(
  startDate: string,
  endDate: string
): { startDate: string; endDate: string } {
  const start = dayjs(startDate);
  const end = dayjs(endDate);
  if (!start.isValid() || !end.isValid()) {
    return { startDate, endDate };
  }
  const days = Math.max(1, end.diff(start, "day") + 1);
  return {
    startDate: start.subtract(days, "day").format("YYYY-MM-DD"),
    endDate: start.subtract(1, "day").format("YYYY-MM-DD"),
  };
}

/* ---------------- daily facts ---------------- */

/**
 * One row per day. Spend is shaped by weekday seasonality, growth and seeded
 * jitter; every other metric is derived from it so the ratios stay consistent.
 */
export function buildMockDailyFacts(
  platform: PaidMediaOverviewPlatform,
  startDate: string,
  endDate: string
): MockDailyFact[] {
  const profile = PLATFORM_PROFILES[platform];
  if (!profile) {
    return [];
  }

  const anchor = dayjs(TREND_ANCHOR_DATE);

  const growthFactor = (date: string, rate: number): number => {
    const days = dayjs(date).diff(anchor, "day");
    const factor = Math.pow(1 + rate, days);
    return Math.min(MAX_GROWTH_FACTOR, Math.max(MIN_GROWTH_FACTOR, factor));
  };

  return mockDateRange(startDate, endDate).map(date => {
    const weekday = dayjs(date).day(); // 0 = Sunday
    const isWeekend = weekday === 4 || weekday === 5 || weekday === 6; // Thu–Sat
    const seasonality = 1 + (isWeekend ? profile.weekendLift : -profile.weekendLift * 0.35);
    const budgetTrend = growthFactor(date, profile.budgetGrowth);
    const spendSwing = 1 + seededSwing(`${platform}|spend|${date}`) * profile.volatility;

    const spend = Math.max(0, profile.dailySpend * seasonality * budgetTrend * spendSwing);

    // Efficiency wobbles independently of budget: a day can be cheap on reach
    // and still convert badly, which is what makes the ROAS and CPA lines move.
    const cpm = profile.cpm * (1 + seededSwing(`${platform}|cpm|${date}`) * 0.14);
    const ctr = profile.ctr * (1 + seededSwing(`${platform}|ctr|${date}`) * 0.18);
    const conversionSwing = seededSwing(`${platform}|cvr|${date}`);
    // Occasional bad day so the CPA spike markers have something to flag.
    const slump = seededRatio(`${platform}|slump|${date}`) > 0.94 ? 0.62 : 1;
    const clickToPurchase =
      profile.clickToPurchase *
      (1 + conversionSwing * 0.2) *
      slump *
      growthFactor(date, profile.efficiencyGrowth);
    const aov = profile.aov * (1 + seededSwing(`${platform}|aov|${date}`) * 0.1);

    const impressions = Math.round((spend / cpm) * 1000);
    const clicks = Math.round(impressions * ctr);
    const purchases = Math.round(clicks * clickToPurchase);
    const revenue = purchases * aov;

    return {
      date,
      spend: round2(spend),
      impressions,
      clicks,
      purchases,
      revenue: round2(revenue),
    };
  });
}

export function sumMockFacts(facts: MockDailyFact[]): MockTotals {
  return (facts ?? []).reduce<MockTotals>(
    (totals, fact) => ({
      spend: round2(totals.spend + (fact?.spend ?? 0)),
      impressions: totals.impressions + (fact?.impressions ?? 0),
      clicks: totals.clicks + (fact?.clicks ?? 0),
      purchases: totals.purchases + (fact?.purchases ?? 0),
      revenue: round2(totals.revenue + (fact?.revenue ?? 0)),
    }),
    { spend: 0, impressions: 0, clicks: 0, purchases: 0, revenue: 0 }
  );
}

export function mockPlatformTotals(
  platform: PaidMediaOverviewPlatform,
  startDate: string,
  endDate: string
): MockTotals {
  return sumMockFacts(buildMockDailyFacts(platform, startDate, endDate));
}


/* ---------------- bucketing ---------------- */

export type MockGranularity = "daily" | "weekly" | "monthly";

const bucketKeyFor = (date: string, granularity: MockGranularity): string => {
  if (granularity === "monthly") {
    return dayjs(date).format("YYYY-MM");
  }
  if (granularity === "weekly") {
    // Monday-anchored buckets, matching how the live endpoint labels weeks.
    const parsed = dayjs(date);
    const offset = (parsed.day() + 6) % 7;
    return parsed.subtract(offset, "day").format("YYYY-MM-DD");
  }
  return date;
};

export function bucketMockFacts(
  facts: MockDailyFact[],
  granularity: MockGranularity
): MockDailyFact[] {
  if (granularity === "daily") {
    return facts;
  }

  const buckets = new Map<string, MockDailyFact>();
  (facts ?? []).forEach(fact => {
    const key = bucketKeyFor(fact.date, granularity);
    const current = buckets.get(key);
    if (!current) {
      buckets.set(key, { ...fact, date: key });
      return;
    }
    buckets.set(key, {
      date: key,
      spend: round2(current.spend + fact.spend),
      impressions: current.impressions + fact.impressions,
      clicks: current.clicks + fact.clicks,
      purchases: current.purchases + fact.purchases,
      revenue: round2(current.revenue + fact.revenue),
    });
  });

  return Array.from(buckets.values()).sort((left, right) => left.date.localeCompare(right.date));
}

/* ---------------- campaigns ---------------- */

export interface MockCampaignSeed {
  platform: PaidMediaOverviewPlatform;
  campaignId: string;
  campaignName: string;
  objectiveOrNetwork: string;
  startDate: string;
  /** Share of the platform's spend for the period. Each platform sums to 1. */
  spendWeight: number;
  /** Campaign ROAS relative to its platform average. */
  roasMultiplier: number;
  /** Campaign AOV relative to its platform average — drives purchase counts. */
  aovMultiplier: number;
}

/** `spendWeight` sums to 1 per platform, so campaigns reconcile with its card. */
export const MOCK_CAMPAIGN_SEEDS: MockCampaignSeed[] = [
  // Meta Ads — scale channel, prospecting-heavy.
  {
    platform: "meta_ads",
    campaignId: "mock-meta-001",
    campaignName: "Riyadh Prospecting — Abaya Edit",
    objectiveOrNetwork: "Sales",
    startDate: "2026-06-14",
    spendWeight: 0.34,
    roasMultiplier: 0.88,
    aovMultiplier: 1.04,
  },
  {
    platform: "meta_ads",
    campaignId: "mock-meta-002",
    campaignName: "Catalogue Retargeting — 14 Day",
    objectiveOrNetwork: "Sales",
    startDate: "2026-05-02",
    spendWeight: 0.21,
    roasMultiplier: 1.62,
    aovMultiplier: 0.92,
  },
  {
    platform: "meta_ads",
    campaignId: "mock-meta-003",
    campaignName: "Advantage+ Shopping — KSA",
    objectiveOrNetwork: "Sales",
    startDate: "2026-07-01",
    spendWeight: 0.22,
    roasMultiplier: 1.12,
    aovMultiplier: 1.1,
  },
  {
    platform: "meta_ads",
    campaignId: "mock-meta-004",
    campaignName: "Eid Gifting — Broad Reach",
    objectiveOrNetwork: "Awareness",
    startDate: "2026-07-19",
    spendWeight: 0.14,
    roasMultiplier: 0.54,
    aovMultiplier: 1.0,
  },
  {
    platform: "meta_ads",
    campaignId: "mock-meta-005",
    campaignName: "Lookalike 2% — New Arrivals",
    objectiveOrNetwork: "Traffic",
    startDate: "2026-08-04",
    spendWeight: 0.09,
    roasMultiplier: 0.74,
    aovMultiplier: 0.88,
  },

  // Google Ads — efficiency leader, brand + shopping.
  {
    platform: "google_ads",
    campaignId: "mock-google-001",
    campaignName: "Brand Search — Exact",
    objectiveOrNetwork: "Search",
    startDate: "2026-01-12",
    spendWeight: 0.24,
    roasMultiplier: 1.74,
    aovMultiplier: 1.02,
  },
  {
    platform: "google_ads",
    campaignId: "mock-google-002",
    campaignName: "Performance Max — Full Catalogue",
    objectiveOrNetwork: "Performance Max",
    startDate: "2026-04-08",
    spendWeight: 0.33,
    roasMultiplier: 1.06,
    aovMultiplier: 1.08,
  },
  {
    platform: "google_ads",
    campaignId: "mock-google-003",
    campaignName: "Shopping — Best Sellers",
    objectiveOrNetwork: "Shopping",
    startDate: "2026-03-21",
    spendWeight: 0.22,
    roasMultiplier: 0.94,
    aovMultiplier: 0.96,
  },
  {
    platform: "google_ads",
    campaignId: "mock-google-004",
    campaignName: "Non-Brand Search — Category Terms",
    objectiveOrNetwork: "Search",
    startDate: "2026-05-30",
    spendWeight: 0.14,
    roasMultiplier: 0.58,
    aovMultiplier: 0.94,
  },
  {
    platform: "google_ads",
    campaignId: "mock-google-005",
    campaignName: "YouTube Demand Gen — Summer",
    objectiveOrNetwork: "Display",
    startDate: "2026-07-11",
    spendWeight: 0.07,
    roasMultiplier: 0.44,
    aovMultiplier: 1.06,
  },

  // TikTok Ads — growth channel, creative-led.
  {
    platform: "tiktok_ads",
    campaignId: "mock-tiktok-001",
    campaignName: "Spark Ads — Creator Haul",
    objectiveOrNetwork: "Product Sales",
    startDate: "2026-06-26",
    spendWeight: 0.31,
    roasMultiplier: 1.34,
    aovMultiplier: 0.98,
  },
  {
    platform: "tiktok_ads",
    campaignId: "mock-tiktok-002",
    campaignName: "Video Shopping Ads — Catalogue",
    objectiveOrNetwork: "Product Sales",
    startDate: "2026-05-18",
    spendWeight: 0.26,
    roasMultiplier: 1.12,
    aovMultiplier: 1.06,
  },
  {
    platform: "tiktok_ads",
    campaignId: "mock-tiktok-003",
    campaignName: "Web Conversions — Retarget 7D",
    objectiveOrNetwork: "Website Conversions",
    startDate: "2026-07-05",
    spendWeight: 0.19,
    roasMultiplier: 1.48,
    aovMultiplier: 0.9,
  },
  {
    platform: "tiktok_ads",
    campaignId: "mock-tiktok-004",
    campaignName: "Reach & Frequency — Brand Lift",
    objectiveOrNetwork: "Reach",
    startDate: "2026-07-28",
    spendWeight: 0.15,
    roasMultiplier: 0.38,
    aovMultiplier: 1.02,
  },
  {
    platform: "tiktok_ads",
    campaignId: "mock-tiktok-005",
    campaignName: "Traffic — Trending Sounds Test",
    objectiveOrNetwork: "Traffic",
    startDate: "2026-08-10",
    spendWeight: 0.09,
    roasMultiplier: 0.62,
    aovMultiplier: 0.86,
  },

  // Snapchat Ads — smallest channel, upper-funnel heavy.
  {
    platform: "snapchat_ads",
    campaignId: "mock-snap-001",
    campaignName: "Dynamic Product Ads — Catalogue",
    objectiveOrNetwork: "Catalog Sales",
    startDate: "2026-06-09",
    spendWeight: 0.34,
    roasMultiplier: 1.42,
    aovMultiplier: 1.02,
  },
  {
    platform: "snapchat_ads",
    campaignId: "mock-snap-002",
    campaignName: "Story Ads — Weekend Drop",
    objectiveOrNetwork: "Website Conversions",
    startDate: "2026-07-02",
    spendWeight: 0.27,
    roasMultiplier: 1.06,
    aovMultiplier: 0.96,
  },
  {
    platform: "snapchat_ads",
    campaignId: "mock-snap-003",
    campaignName: "Collection Ads — Gifting",
    objectiveOrNetwork: "Catalog Sales",
    startDate: "2026-07-24",
    spendWeight: 0.18,
    roasMultiplier: 0.86,
    aovMultiplier: 1.08,
  },
  {
    platform: "snapchat_ads",
    campaignId: "mock-snap-004",
    campaignName: "AR Lens — Try On Experience",
    objectiveOrNetwork: "Awareness",
    startDate: "2026-08-01",
    spendWeight: 0.13,
    roasMultiplier: 0.42,
    aovMultiplier: 1.0,
  },
  {
    platform: "snapchat_ads",
    campaignId: "mock-snap-005",
    campaignName: "Snap Discover — Reach Test",
    objectiveOrNetwork: "Reach",
    startDate: "2026-08-13",
    spendWeight: 0.08,
    roasMultiplier: 0.55,
    aovMultiplier: 0.9,
  },
];

export interface MockCampaignMetrics {
  platform: PaidMediaOverviewPlatform;
  campaignId: string;
  campaignName: string;
  objectiveOrNetwork: string;
  startDate: string;
  spend: number;
  impressions: number;
  clicks: number;
  purchases: number;
  revenue: number;
}

/**
 * Split a platform's period totals across its campaigns: spend by weight,
 * revenue by weight x ROAS multiplier, both normalised back to the totals.
 */
export function buildMockCampaignMetrics(
  platform: PaidMediaOverviewPlatform,
  totals: MockTotals
): MockCampaignMetrics[] {
  const seeds = MOCK_CAMPAIGN_SEEDS.filter(seed => seed.platform === platform);
  if (!seeds.length) {
    return [];
  }

  const revenueWeights = seeds.map(seed => seed.spendWeight * seed.roasMultiplier);
  const revenueWeightTotal = revenueWeights.reduce((sum, weight) => sum + weight, 0) || 1;

  // Purchases follow revenue but are damped by the campaign's AOV: a campaign
  // with a higher basket earns the same revenue from fewer orders.
  const purchaseWeights = seeds.map(
    (seed, index) => revenueWeights[index] / (seed.aovMultiplier || 1)
  );
  const purchaseWeightTotal = purchaseWeights.reduce((sum, weight) => sum + weight, 0) || 1;

  return seeds.map((seed, index) => {
    const revenueShare = revenueWeights[index] / revenueWeightTotal;
    const purchaseShare = purchaseWeights[index] / purchaseWeightTotal;

    return {
      platform,
      campaignId: seed.campaignId,
      campaignName: seed.campaignName,
      objectiveOrNetwork: seed.objectiveOrNetwork,
      startDate: seed.startDate,
      spend: round2(totals.spend * seed.spendWeight),
      impressions: Math.round(totals.impressions * seed.spendWeight),
      clicks: Math.round(totals.clicks * seed.spendWeight),
      purchases: Math.round(totals.purchases * purchaseShare),
      revenue: round2(totals.revenue * revenueShare),
    };
  });
}

/** Daily spend/revenue split for one campaign — used by the campaign drawer. */
export function buildMockCampaignDailyFacts(
  platform: PaidMediaOverviewPlatform,
  campaignId: string,
  startDate: string,
  endDate: string
): MockDailyFact[] {
  const seed = MOCK_CAMPAIGN_SEEDS.find(
    candidate => candidate.platform === platform && candidate.campaignId === campaignId
  );
  if (!seed) {
    return [];
  }

  const platformFacts = buildMockDailyFacts(platform, startDate, endDate);
  const revenueSeeds = MOCK_CAMPAIGN_SEEDS.filter(candidate => candidate.platform === platform);
  const revenueWeightTotal =
    revenueSeeds.reduce(
      (sum, candidate) => sum + candidate.spendWeight * candidate.roasMultiplier,
      0
    ) || 1;
  const revenueShare = (seed.spendWeight * seed.roasMultiplier) / revenueWeightTotal;

  return platformFacts.map(fact => {
    // A campaign's daily share drifts a little around its period share so the
    // drawer's sparkline isn't a scaled copy of the platform curve.
    const wobble = 1 + seededSwing(`${campaignId}|${fact.date}`) * 0.18;
    const spendShare = seed.spendWeight * wobble;
    const dailyRevenueShare = revenueShare * wobble;

    return {
      date: fact.date,
      spend: round2(fact.spend * spendShare),
      impressions: Math.round(fact.impressions * spendShare),
      clicks: Math.round(fact.clicks * spendShare),
      purchases: Math.round(fact.purchases * dailyRevenueShare),
      revenue: round2(fact.revenue * dailyRevenueShare),
    };
  });
}

/* ---------------- store revenue validation ---------------- */

export interface MockValidationProfile {
  /** Over-reporting versus the store, as a fraction of reported revenue. */
  overReporting: number;
  /** Share of validated gross revenue lost to refunds and cancellations. */
  refundRate: number;
}

export const MOCK_VALIDATION_PROFILES: Record<
  PaidMediaOverviewPlatform,
  MockValidationProfile
> = {
  meta_ads: { overReporting: 0.13, refundRate: 0.07 },
  google_ads: { overReporting: 0.06, refundRate: 0.05 },
  tiktok_ads: { overReporting: 0.16, refundRate: 0.09 },
  snapchat_ads: { overReporting: 0.19, refundRate: 0.11 },
};

/* ---------------- attribution confidence ---------------- */

export interface MockAttributionConfidence {
  eligibleOrders: number;
  confidence: number;
  recognizedSource: number;
  campaignId: number;
  adId: number;
  usableUtm: number;
}

/** Scored against the window's order volume so the card moves with the range. */
export function buildMockAttributionConfidence(
  totalPurchases: number
): MockAttributionConfidence {
  const eligibleOrders = Math.round(totalPurchases * 0.86);
  return {
    eligibleOrders,
    confidence: 78.4,
    recognizedSource: Math.round(eligibleOrders * 0.94),
    campaignId: Math.round(eligibleOrders * 0.71),
    adId: Math.round(eligibleOrders * 0.52),
    usableUtm: Math.round(eligibleOrders * 0.83),
  };
}
